import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * MacroDonut — Protein/Carbs/Fat breakdown donut with a calorie total in the
 * center and a grams + % legend. Extracted from the staff Analytics "Macro
 * Breakdown" panel for reuse on the client mobile Home (Zone 2) and elsewhere.
 *
 * Center shows the calorie total (the sum the slices break down), distinct from
 * a calorie progress bar (which is target-relative).
 */

interface MacroDonutProps {
  proteinG: number;
  carbsG: number;
  fatsG: number;
  calories: number;
}

const PROTEIN_COLOR = '#3b82f6';
const CARBS_COLOR = '#10b981';
const FAT_COLOR = '#f59e0b';

export const MacroDonut: React.FC<MacroDonutProps> = ({ proteinG, carbsG, fatsG, calories }) => {
  const proteinCal = proteinG * 4;
  const carbsCal = carbsG * 4;
  const fatCal = fatsG * 9;
  const totalMacroCal = proteinCal + carbsCal + fatCal;

  const proteinPct = totalMacroCal > 0 ? Math.round((proteinCal / totalMacroCal) * 100) : 0;
  const carbsPct = totalMacroCal > 0 ? Math.round((carbsCal / totalMacroCal) * 100) : 0;
  const fatPct = totalMacroCal > 0 ? 100 - proteinPct - carbsPct : 0;

  const data = useMemo(() => ({
    labels: ['Protein', 'Carbs', 'Fat'],
    datasets: [{
      data: totalMacroCal > 0 ? [proteinCal, carbsCal, fatCal] : [1, 1, 1],
      backgroundColor: totalMacroCal > 0
        ? [PROTEIN_COLOR, CARBS_COLOR, FAT_COLOR]
        : ['#374151', '#374151', '#374151'],
      borderColor: 'transparent',
      borderWidth: 0,
    }],
  }), [proteinCal, carbsCal, fatCal, totalMacroCal]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const pct = totalMacroCal > 0 ? Math.round((ctx.raw / totalMacroCal) * 100) : 0;
            return `${ctx.label}: ${pct}%`;
          },
        },
      },
    },
  }), [totalMacroCal]);

  if (totalMacroCal <= 0) {
    return <div className="mc-macro-donut-empty">No nutrition data yet today.</div>;
  }

  return (
    <div className="mc-macro-donut">
      <div className="mc-macro-donut-chart">
        <Doughnut data={data} options={options} />
        <div className="mc-macro-donut-center">
          <span className="mc-macro-donut-cal">{Math.round(calories)}</span>
          <span className="mc-macro-donut-unit">cal</span>
        </div>
      </div>
      <div className="mc-macro-donut-legend">
        <div className="mc-macro-legend-item">
          <span className="mc-macro-dot" style={{ background: PROTEIN_COLOR }} />
          <span className="mc-macro-name">Protein</span>
          <span className="mc-macro-val">{Math.round(proteinG)}g ({proteinPct}%)</span>
        </div>
        <div className="mc-macro-legend-item">
          <span className="mc-macro-dot" style={{ background: CARBS_COLOR }} />
          <span className="mc-macro-name">Carbs</span>
          <span className="mc-macro-val">{Math.round(carbsG)}g ({carbsPct}%)</span>
        </div>
        <div className="mc-macro-legend-item">
          <span className="mc-macro-dot" style={{ background: FAT_COLOR }} />
          <span className="mc-macro-name">Fat</span>
          <span className="mc-macro-val">{Math.round(fatsG)}g ({fatPct}%)</span>
        </div>
      </div>
    </div>
  );
};
